// Shared imports
const articleUtils = require('shared/model-helpers/article')
// Client V1 imports
const airbrake = require('client/lib/airbrake')
const { track } = require('client/lib/analytics')
const { send: ajax } = require('client/lib/request')
const { openWindow } = require('client/lib/window')
// Common Client v2 imports
const { navigate } = require('client-v2/common/state/router/actions')
// Sibling imports
const {
  cacheReportedUrlId,
  getCachedReportedUrlIds
} = require('./utils')

// Action types

const ARTICLE_LOADED = 'ARTICLE_LOADED'
const ARTICLE_MIXES_UPDATED = 'ARTICLE_MIXES_UPDATED'
const ARTICLE_DOMAIN_BLOCKED = 'ARTICLE_DOMAIN_BLOCKED'
const ARTICLES_REPORTED = 'ARTICLES_REPORTED'

// Action creators

function articleMixesUpdated(urlId, mixIds) {
  return {
    type: ARTICLE_MIXES_UPDATED,
    payload: {
      urlId,
      mixIds
    }
  }
}

function articleLoaded({ article, urlId }) {
  return {
    type: ARTICLE_LOADED,
    payload: { article, urlId }
  }
}

// Action dispatchers

function fetchArticle({ article: { hash } }) {
  return function (dispatch) {
    ajax({ url: `/api/v0.2/url/${hash}` })
    .then(result => {
      const article = result.url
      dispatch(articleLoaded({
        article,
        urlId: article['url_id']
      }))
    })
    .catch(err => {
      airbrake.notify(err)
    })
  }
}

function applyReportedArticlesCache() {
  return function (dispatch) {
    const urlIds = getCachedReportedUrlIds()
    if (urlIds.length) {
      dispatch({
        type: ARTICLES_REPORTED,
        payload: { urlIds }
      })
    }
  }
}

function navigateToArticleContent(options) {
  if (options.event && options.event.altKey) {
    return navigateToArticleProfile(options)
  }
  return function () {
    const { article, pageContext } = options
    openWindow(article.url)
    track('show-web-content', {
      url: article.url,
      hostname: article.hostname,
      metabase: {
        'actioned_object_type': 'url_id',
        'actioned_object_id': article['url_id']
      },
      'page_context': pageContext
    })
  }
}

function navigateToArticleDomainSearch({ article, event, pageContext }) {
  return navigate({
    event,
    href: articleDomainSearchHref(article),
    pageContext
  })
}

function navigateToArticleProfile({ article, event, pageContext }) {
  return navigate({
    event,
    href: articleProfileHref(article),
    pageContext
  })
}

function showArticleActionsModal({ article, pageContext }) {
  // Mitigate circular dependency
  const clientV1Bridge = require('client-v2/common/client-v1-bridge')
  return function (dispatch) {
    clientV1Bridge.showArticleActionsModal({ article, pageContext })
    .then(reason => {
      if (reason === 'block') {
        dispatch({
          type: ARTICLE_DOMAIN_BLOCKED,
          payload: {
            hostname: article.hostname
          }
        })
      } else if (reason) {
        const urlId = article['url_id']
        cacheReportedUrlId(urlId)
        dispatch({
          type: ARTICLES_REPORTED,
          payload: {
            urlIds: [urlId]
          }
        })
      }
    })
    .catch(err => { airbrake.notify(err) })
  }
}

function showArticleProfileModal(options) {
  // Mitigate circular dependencies
  const articleProfileModalActions = require('client-v2/common/containers/article-profile/state/modal-actions')
  // Allow opening article profile in a new tab instead of popping the modal
  if (options.event && options.event.altKey) {
    return navigateToArticleProfile(options)
  }
  return function (dispatch, getState) {
    const { articleProfileModal } = getState()
    if (typeof articleProfileModal === 'undefined') {
      // Fall-back to article profile page if modal is not available
      return dispatch(navigateToArticleProfile(options))
    }
    return dispatch(articleProfileModalActions.show(options))
  }
}

function showArticleShareModal({ article }) {
  // Mitigate circular dependency
  const clientV1Bridge = require('client-v2/common/client-v1-bridge')
  return function () {
    clientV1Bridge.showArticleShareModal({ article })
  }
}

function showMixSelectModal({ article, pageContext = {} }) {
  // Mitigate circular dependency
  const clientV1Bridge = require('client-v2/common/client-v1-bridge')
  return function () {
    clientV1Bridge.showMixSelectModal({
      article,
      recId: pageContext.recId,
      recBatchId: pageContext.recBatchId
    })
  }
}

function trackCardView({ article, pageContext }) {
  return function () {
    track('view-card', {
      metabase: {
        'actioned_object_type': 'url_id',
        'actioned_object_id': article['url_id']
      },
      'page_context': pageContext
    })
  }
}

function trackGridView({ article, pageContext, recPosition }) {
  return function (dispatch, getState) {
    const { user } = getState()
    if (user.loggedIn) {
      track('view-grid', {
        metabase: {
          'actioned_object_type': 'url_id',
          'actioned_object_id': article['url_id'],
          'actioned_object_position': recPosition
        },
        'page_context': pageContext
      })
    }
  }
}

// Helpers

function articleProfileHref(article) {
  return articleUtils.prettyPath(article.hash, article.title)
}

function articleDomainSearchHref(article) {
  return `/search/${article.hostname}/domain`
}

// Action exports

module.exports = {
  // Types
  ARTICLE_DOMAIN_BLOCKED,
  ARTICLE_LOADED,
  ARTICLE_MIXES_UPDATED,
  ARTICLES_REPORTED,
  // Creators
  articleLoaded,
  articleMixesUpdated,
  // Dispatchers
  applyReportedArticlesCache,
  fetchArticle,
  navigateToArticleContent,
  navigateToArticleDomainSearch,
  navigateToArticleProfile,
  showArticleActionsModal,
  showArticleProfileModal,
  showArticleShareModal,
  showMixSelectModal,
  trackCardView,
  trackGridView,
  // Helpers
  articleProfileHref,
  articleDomainSearchHref
}
